import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// IMPORTANT: import from /pure (not @stripe/stripe-js) so the script
// tag for js.stripe.com is NOT injected as a side effect of this
// module being imported. Stripe.js will only load once getStripe()
// below is called (= the user actually opens this page with a valid
// clientSecret). The non-pure entry would inject js.stripe.com on
// every page in the app because App.tsx eagerly imports this file,
// which is what was triggering the r.stripe.com/b CORS noise on
// /mock, /admin/interviews, etc.
import { loadStripe } from '@stripe/stripe-js/pure';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import { ArrowLeft, Shield, Tag, Clock } from 'lucide-react';
import { useUserProfile } from '../contexts/UserProfileContext';

const LAUNCH_OFFER_DEADLINE = new Date('2026-04-26T23:59:59');

function useCountdown(deadline: Date) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const diff = deadline.getTime() - now.getTime();
  if (diff <= 0) return null;

  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
  };
}

// Lazy-init: Stripe.js was previously loaded at module-evaluation time
// which made the global stripe analytics iframe attach to EVERY page in
// the app -- not just /stripe-checkout. That triggered the
// "r.stripe.com/b CORS multiple values" console error on /mock, /admin,
// etc. Holding the promise in a module-scoped variable but only
// initialising it inside the component means the script only loads when
// the user actually navigates to this page.
let _stripePromise: ReturnType<typeof loadStripe> | null = null;
function getStripe() {
  if (!_stripePromise) {
    _stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');
  }
  return _stripePromise;
}

const StripeCheckoutPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('pricing');
  const { userProfile } = useUserProfile();
  const countdown = useCountdown(LAUNCH_OFFER_DEADLINE);
  const isOfferActive = countdown !== null;

  const clientSecret = (location.state as any)?.clientSecret;
  const planType = (location.state as any)?.planType;

  if (!clientSecret) {
    navigate('/pricing');
    return null;
  }
  // Triggered only when this page actually renders with a valid
  // clientSecret -- so any other route never injects Stripe's iframe.
  const stripePromise = getStripe();

  const planName = planType === 'recruiter_premium' ? 'Recruiter Premium' : 'Explorer Premium';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-3 sm:py-6 px-3 sm:px-4">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <button
            onClick={() => navigate('/pricing')}
            className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-xs sm:text-sm"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {t('checkout.backToPricing')}
          </button>
          <div className="flex items-center gap-1 text-gray-400 text-[10px] sm:text-xs">
            <Shield className="w-3 h-3" />
            {t('checkout.secure')}
          </div>
        </div>

        <div className="mb-3 sm:mb-4">
          <h1 className="text-base sm:text-lg font-semibold text-gray-900">
            {planName}
          </h1>
          <p className="text-gray-500 text-xs sm:text-sm mt-0.5">
            {t('checkout.subtitle')}
          </p>
        </div>

        {isOfferActive && (
          <div className="flex flex-col items-center gap-2 mb-3 sm:mb-4">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-50 via-rose-50 to-pink-50 border border-pink-200 rounded-full px-3.5 py-1.5 sm:px-4 sm:py-2 shadow-sm">
              <Tag className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-pink-500 flex-shrink-0" />
              <span className="text-[11px] sm:text-xs text-gray-700">
                {t('promo.text')}{' '}
                <span className="font-bold text-pink-600 bg-pink-100 px-1.5 py-0.5 rounded font-mono tracking-wide">
                  {t('promo.code')}
                </span>
              </span>
              <span className="text-[9px] sm:text-[10px] font-bold text-white bg-pink-500 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                {t('promo.badge')}
              </span>
            </div>

            <div className="flex items-center gap-1.5 text-gray-500 text-[10px] sm:text-[11px]">
              <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
              <span>{t('countdown.endsIn')}</span>
              <div className="flex items-center gap-0.5 font-mono font-semibold text-gray-800">
                <span className="bg-gray-900 text-white px-1 py-0.5 rounded text-[10px] sm:text-[11px] min-w-[24px] text-center">
                  {countdown!.days}{t('countdown.days')}
                </span>
                <span className="text-gray-300">:</span>
                <span className="bg-gray-900 text-white px-1 py-0.5 rounded text-[10px] sm:text-[11px] min-w-[24px] text-center">
                  {String(countdown!.hours).padStart(2, '0')}{t('countdown.hours')}
                </span>
                <span className="text-gray-300">:</span>
                <span className="bg-gray-900 text-white px-1 py-0.5 rounded text-[10px] sm:text-[11px] min-w-[24px] text-center">
                  {String(countdown!.minutes).padStart(2, '0')}{t('countdown.minutes')}
                </span>
                <span className="text-gray-300">:</span>
                <span className="bg-gray-900 text-white px-1 py-0.5 rounded text-[10px] sm:text-[11px] min-w-[24px] text-center">
                  {String(countdown!.seconds).padStart(2, '0')}{t('countdown.seconds')}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{ clientSecret }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    </div>
  );
};

export default StripeCheckoutPage;
